import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useTranslation } from 'react-i18next';
import Stepper, { EStepState } from "@components/new/Stepper";
import { Modal, ModalSize } from "@components/new/Modal";
import { MessageModalContent } from '@components/modals/MessageModalNew';
import DelLinkModal from "../DelStoreModal/DelLinkModal";
import Form from "./Form";
import {
  updateWbProducts,
  updateYmProducts,
  TSellerWarehouse,
  TWarehouse,
} from "@api/apiClient/fbp";
// import { AlertType } from "@components/new/Alert";
import { FormHandle, IWBInfoItem, IYMInfoItem } from "./types";

interface AddWarehouseModalProps {
  onClose: () => void;
  onSuccess?: (newSellerWarehouseName: string) => void;
  warehouse: TWarehouse;
  sellerWarehouse?: TSellerWarehouse;
  ymItems?: IYMInfoItem[];
  wbItems?: IWBInfoItem[];
  isNewLinkToMove?: boolean;
}

interface ISubmitButtonProps {
  disabled: boolean;
  isLoading?: boolean;
}

enum steps {
  FORM = "form",
  SUCCESS = "success",
  ERROR = "error",
}

const AddWarehouseModal = ({ onClose, onSuccess, warehouse, sellerWarehouse, isNewLinkToMove, ymItems, wbItems }: AddWarehouseModalProps) => {
  const [step, setStep] = useState(steps.FORM);
  const [ buttonProps, setButtonProps ] = useState<ISubmitButtonProps>({
    disabled: false,
    isLoading: false,
  });
  const [isOpenDelWindow, setOpenDelWindow] = useState(false);
  const [ headerMessage, setHeaderMessage ] = useState<React.ReactNode>();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formRef = useRef<FormHandle>({ submit: () => { } });

  const handleButtonProps = (props: ISubmitButtonProps) => {
    setButtonProps({
      disabled: props.disabled ?? false,
      isLoading: props.isLoading ?? false,
    })
  }

  const updateWbMutation = useMutation({
    mutationFn: (wbSellerId: string) => updateWbProducts(wbSellerId),
  });

  const updateYmMutation = useMutation({
    mutationFn: (ymSellerId: string) => updateYmProducts(ymSellerId),
  });

  const handleSuccess = ({ ymSellerIds, wbSellerIds, newSellerWarehouseName }: {
    ymSellerIds: string[];
    wbSellerIds: string[];
    newSellerWarehouseName: string;
  }) => {
    queryClient.invalidateQueries(
      { queryKey: ["getFBPRequests"] },
    );
    queryClient.invalidateQueries(
      { queryKey: ["getWarehouses"] },
    );
    wbSellerIds.forEach((wbSellerId) => updateWbMutation.mutate(wbSellerId));
    ymSellerIds.forEach((ymSellerId) => updateYmMutation.mutate(ymSellerId));
    if (onSuccess) {
      onSuccess(newSellerWarehouseName);
    } else {
      setStep(steps.SUCCESS);
    }
  }

  const onError = () => {
    setStep(steps.ERROR);
  }

  const title = sellerWarehouse ? t('fbpPage.addWarehouseModal.title') : t('fbpPage.addWarehouseModal.titleNew')

  const stepsData = [
    { text: t(`fbpPage.addWarehouseModal.step-1-${sellerWarehouse ? 'edit' : 'new'}`), state: EStepState.ENABLED },
    {
      text: t(`fbpPage.addWarehouseModal.step-2-${sellerWarehouse ? 'edit' : 'new'}`),
      state: (step === steps.SUCCESS ? EStepState.SUCCESS : (
        step === steps.ERROR ? EStepState.ERROR : EStepState.DISABLED
      )),
    },
  ]

  if (step === steps.SUCCESS) {
    return (
      <Modal
        isOpen={true}
        title={title}
        headerContent={<Stepper steps={stepsData} gap="small" />}
        onClose={onClose}
        size={ModalSize.L}
        footerButtons={[
          {
            title: t('fbpPage.addWarehouseModal.successCloseBtn'),
            onClick: onClose,
          },
        ]}
      >
        <MessageModalContent
          title={t('fbpPage.addWarehouseModal.successTitle')}
          description={t('fbpPage.addWarehouseModal.successDescription')}
          variant="success"
        />
      </Modal>
    )
  }

  if (step === steps.ERROR) {
    return (
      <Modal
        isOpen={true}
        title={title}
        headerContent={<Stepper steps={stepsData} gap="small" />}
        onClose={onClose}
        size={ModalSize.L}
        footerButtons={[
          {
            title: t('fbpPage.addWarehouseModal.errorCancelBtn'),
            onClick: onClose,
          },
          {
            title: t('fbpPage.addWarehouseModal.errorRepeateBtn'),
            onClick: () => setStep(steps.FORM),
          },
        ]}
      >
        <MessageModalContent
          title={t('fbpPage.addWarehouseModal.errorTitle')}
          description={t('fbpPage.addWarehouseModal.errorDescription')}
          variant="warning"
        />
      </Modal>
    )
  }

  return (
    <>
      <Modal
        isOpen={true}
        title={title}
        headerContent={<Stepper steps={stepsData} gap="small" />}
        size={ModalSize.L}
        onClose={onClose}
        confirmCancel
        headerTitleContent={headerMessage}
        footerButtons={[
          ...((isNewLinkToMove || !sellerWarehouse) ? [
            {
              title: t('fbpPage.addWarehouseModal.closeBtn'),
            },
          ] : [
            {
              title: t('fbpPage.addWarehouseModal.removeBtn'),
              onClick: () => {
                setOpenDelWindow(true);
              },
            },
          ]),
          {
            title: sellerWarehouse ? t('fbpPage.addWarehouseModal.updateBtn') : t('fbpPage.addWarehouseModal.createBtn'),
            onClick: () => {
              formRef.current && formRef.current.submit();
            },
            disabled: buttonProps.disabled,
            loading: buttonProps.isLoading,
          },
        ]}
      >
        <Form
          formRef={formRef}
          onStatusChange={handleButtonProps}
          onSuccess={handleSuccess}
          onError={onError}
          warehouse={warehouse}
          sellerWarehouseId={sellerWarehouse?.id}
          ymItems={ymItems}
          wbItems={wbItems}
          isNewLinkToMove={isNewLinkToMove}
          setHeaderMessage={setHeaderMessage}
          onCloseWindow={() => onClose()}
        />
      </Modal>
      {isOpenDelWindow ? (
        <DelLinkModal
          onClose={() => setOpenDelWindow(false)}
          //warehouse={warehouse}
          sellerWarehouseId={sellerWarehouse?.id ?? ""}
          stores={[
            ...(ymItems ?? []).map(v => v.name),
            ...(wbItems ?? []).map(v => v.sellerName),
          ]}
          onError={() => {
            setOpenDelWindow(false);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries(
              { queryKey: ["getFBPRequests"] },
            );
            queryClient.invalidateQueries(
              { queryKey: ["getWarehouses"] },
            );
            queryClient.invalidateQueries(
              { queryKey: ["getBlockedWarehouses"] },
            );
            queryClient.invalidateQueries(
              { queryKey: ["getFBPWithdrawalRequests"] },
            );
            queryClient.invalidateQueries(
              { queryKey: ["getSellerDetails"] },
            );
            setOpenDelWindow(false);
            onClose();
            setTimeout(() => {
              queryClient.invalidateQueries(
                { queryKey: ["getWarehouses"] },
              );
              queryClient.invalidateQueries(
                { queryKey: ["getBlockedWarehouses"] },
              );
              queryClient.invalidateQueries(
                { queryKey: ["getSellerDetails"] },
              );
            }, 1000)
            /*setHeaderMessage(<Alert
              type={AlertType.SUCCESS}
              title={t("fbpPage.delStoreModal.willBeDeletedLink")}
            />);*/
          }}
        />
      ) : null}
    </>
  )
}

export default AddWarehouseModal;
